import {
  TrackingDataFieldTypeAccountType,
  TrackingDataFieldTypeLoginMethod,
} from '@shopee-user/tracking-login/src/types';
import {
  TrackingDataFieldTypeSignupMethod,
  TrackingDataFieldTypeLoginMethod as TrackingSignupDataFieldTypeLoginMethod,
} from '@shopee-user/tracking-signup/src/types';
import {
  getAuthKeyTypeFromAuthKey,
  AuthKeyType,
} from '@tob/shared/utils/auth-key';
import { AccType } from '@tob/shared/monitoring/business-metric/enums';
import { sha256 } from '../utils/crypto';

export const hashUserId = (userId: number) =>
  sha256(userId.toString()).toString();

const LOGIN_TRACKING_CONTEXT_KEY = '@shopee-user/tob/login_tracking_context';

export type LoginSignupTrackingContext = {
  signupMethod?: TrackingDataFieldTypeSignupMethod;
  loginMethod?: TrackingDataFieldTypeLoginMethod;
  accountType?: TrackingDataFieldTypeAccountType;
  userId?: number;
};

export const saveLoginSignupTrackingContextToLocal = ({
  signupMethod,
  loginMethod,
  accountType,
  userId,
}: LoginSignupTrackingContext) => {
  sessionStorage.setItem(
    LOGIN_TRACKING_CONTEXT_KEY,
    JSON.stringify({ signupMethod, loginMethod, accountType, userId })
  );
};

export const getLoginSignupTrackingContextFromLocal = ({
  autoClear = false,
}: { autoClear?: boolean } = {}) => {
  const context = sessionStorage.getItem(LOGIN_TRACKING_CONTEXT_KEY);
  if (autoClear) {
    clearLoginSignupTrackingContextFromLocal();
  }
  try {
    return context ? (JSON.parse(context) as LoginSignupTrackingContext) : null;
  } catch {
    clearLoginSignupTrackingContextFromLocal();
    return null;
  }
};

export const clearLoginSignupTrackingContextFromLocal = () => {
  sessionStorage.removeItem(LOGIN_TRACKING_CONTEXT_KEY);
};

// TODO: tracking-login and tracking-signup each declare their own
// TrackingDataFieldTypeLoginMethod enum with identical Email/Phone members.
// Unify them into a single shared enum so we don't need one conversion
// function per package.
export const toTrackingLoginMethod = (
  authKey: string
): TrackingDataFieldTypeLoginMethod =>
  getAuthKeyTypeFromAuthKey(authKey) === AuthKeyType.Email
    ? TrackingDataFieldTypeLoginMethod.Email
    : TrackingDataFieldTypeLoginMethod.Phone;

export const toSignupTrackingLoginMethod = (
  authKey: string
): TrackingSignupDataFieldTypeLoginMethod =>
  getAuthKeyTypeFromAuthKey(authKey) === AuthKeyType.Email
    ? TrackingSignupDataFieldTypeLoginMethod.Email
    : TrackingSignupDataFieldTypeLoginMethod.Phone;

export const toTrackingSignupMethod = (
  authKey: string
): TrackingDataFieldTypeSignupMethod =>
  getAuthKeyTypeFromAuthKey(authKey) === AuthKeyType.Email
    ? TrackingDataFieldTypeSignupMethod.Email
    : TrackingDataFieldTypeSignupMethod.Phone;

export const toTrackingAccountType = (
  accType: AccType
): TrackingDataFieldTypeAccountType =>
  accType === AccType.Tob
    ? TrackingDataFieldTypeAccountType.TobAccount
    : TrackingDataFieldTypeAccountType.OneCredential;
